import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Box,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import useUpdate from "../../../../api/useUpdate";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";

const EditAsset = ({ open, onClose, editData = {} }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const { handleUpdate, loading, success } = useUpdate("general-stock", token);

  const [image, setImage] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    quantity: "",
    purchase_date: "",
    cost_per_unit: "",
    location: "",
    description: "",
  });

  const [errors, setErrors] = useState({});
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || "",
        quantity: editData.quantity || "",
        purchase_date: editData.purchase_date || "",
        cost_per_unit: editData.cost_per_unit || "",
        location: editData.location || "",
        description: editData.description || "",
      });
      setPreviewImage(editData.image || null);
    }
  }, [editData]);

  useEffect(() => {
    if (success) {
      setFormData({
        name: "",
        quantity: "",
        purchase_date: "",
        cost_per_unit: "",
        location: "",
        description: "",
      });
      setPreviewImage(null);
      setImage(null);
      onClose();
    }
  }, [success]);

  const validate = () => {
    let tempErrors = {};
    if (!formData.name) tempErrors.name = t("hr.nameRequired");
    if (!formData.quantity || formData.quantity <= 0)
      tempErrors.quantity = t("hr.quantityRequired");
    if (!formData.purchase_date)
      tempErrors.purchase_date = t("hr.purchaseDateRequired");
    if (!formData.cost_per_unit || formData.cost_per_unit <= 0)
      tempErrors.cost_per_unit = t("hr.costPerUnitRequired");
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const { files } = e.target;

    if (files && files[0]) {
      const file = files[0];
      // Update formData and preview
      setImage(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSave = () => {
    if (validate()) {
      const submitDate = new FormData();

      submitDate.append("name", formData.name);
      submitDate.append("quantity", formData.quantity);
      submitDate.append("purchase_date", formData.purchase_date);
      submitDate.append("cost_per_unit", formData.cost_per_unit);
      submitDate.append("location", formData.location);
      submitDate.append("description", formData.description);
      if (image) {
        submitDate.append("image", image);
      }

      handleUpdate(editData.id, submitDate);
    }
  };

  const clearImage = () => {
    setFormData({ ...formData, image: null });
    setPreviewImage(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          {t("hr.editAsset")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("hr.name")}
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("hr.quantity")}
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              type="number"
              fullWidth
              error={!!errors.quantity}
              helperText={errors.quantity}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("hr.purchaseDate")}
              name="purchase_date"
              value={formData.purchase_date}
              onChange={handleChange}
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              error={!!errors.purchase_date}
              helperText={errors.purchase_date}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("hr.costPerUnit")}
              name="cost_per_unit"
              value={formData.cost_per_unit}
              onChange={handleChange}
              type="number"
              fullWidth
              error={!!errors.cost_per_unit}
              helperText={errors.cost_per_unit}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("hr.location")}
              name="location"
              value={formData.location}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("hr.description")}
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Typography>{t("hr.currentFile")}</Typography>
            {previewImage && (
              <Box sx={{ my: 2, position: "relative" }}>
                <img
                  src={previewImage}
                  alt="Preview"
                  style={{
                    width: "10vw",
                    maxHeight: "10vh",
                    objectFit: "contain",
                  }}
                />
                <IconButton
                  color="error"
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    backgroundColor: "white",
                  }}
                  onClick={clearImage}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mt: 2 }}
            >
              {t("hr.uploadImage")}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>
            {errors.image && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {errors.image}
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">
          {t("hr.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          startIcon={loading && <CircularProgress size={20} />}
          disabled={loading}
        >
          {t("hr.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAsset;
